/**
 * Thin wrapper around the site-wide Meta Pixel (loaded once in MetaPixel.tsx).
 *
 * - fire-once guard per logical key, so route changes / React re-renders and
 *   double submits cannot double-count a conversion
 * - queues events until `fbq` exists (the pixel loads on idle)
 * - custom parameters stay non-PII
 */

type Fbq = (...args: unknown[]) => void;

const FIRED_KEY = "durafoam_meta_events";
const firedKeys = new Set<string>();

function fbq(): Fbq | null {
  if (typeof window === "undefined") return null;
  const f = (window as unknown as { fbq?: Fbq }).fbq;
  return typeof f === "function" ? f : null;
}

function gtag(): Fbq | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: Fbq }).gtag;
  return typeof g === "function" ? g : null;
}

/** Returns true the first time a key is seen; false on every repeat. */
function claim(key: string): boolean {
  if (typeof window === "undefined") return false;
  if (firedKeys.has(key)) return false;
  firedKeys.add(key);
  try {
    const raw = window.sessionStorage.getItem(FIRED_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (list.includes(key)) return false;
    list.push(key);
    window.sessionStorage.setItem(FIRED_KEY, JSON.stringify(list.slice(-40)));
  } catch {
    /* sessionStorage unavailable — in-memory guard still applies */
  }
  return true;
}

const queue: Array<() => void> = [];
let draining = false;

function drain() {
  if (draining || typeof window === "undefined") return;
  draining = true;
  const tick = (attempt: number) => {
    if (fbq()) {
      while (queue.length) queue.shift()?.();
      draining = false;
      return;
    }
    if (attempt > 40) {
      // ~10s without a pixel: give up rather than leaking timers.
      queue.length = 0;
      draining = false;
      return;
    }
    window.setTimeout(() => tick(attempt + 1), 250);
  };
  tick(0);
}

function send(run: () => void) {
  if (typeof window === "undefined") return;
  if (fbq()) {
    try {
      run();
    } catch {
      /* never break the page for analytics */
    }
    return;
  }
  queue.push(run);
  drain();
}

type Params = Record<string, string | number | boolean | undefined>;

function cleanParams(p?: Params): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(p ?? {})) {
    if (v !== undefined && v !== "") out[k] = v;
  }
  return out;
}

/** Standard pixel event, fired at most once per `key`. */
export function trackStandard(
  eventName: "ViewContent" | "Lead" | "Contact",
  key: string,
  params?: Params,
  eventId?: string,
): boolean {
  if (!claim(key)) return false;
  send(() => {
    const f = fbq();
    if (!f) return;
    if (eventId) f("track", eventName, cleanParams(params), { eventID: eventId });
    else f("track", eventName, cleanParams(params));
  });
  return true;
}

/** Custom pixel event, fired at most once per `key`. */
export function trackCustomOnce(
  eventName: "RoofEstimateStarted" | "RoofEstimateCompleted",
  key: string,
  params?: Params,
  eventId?: string,
): boolean {
  if (!claim(key)) return false;
  send(() => {
    const f = fbq();
    if (!f) return;
    if (eventId) f("trackCustom", eventName, cleanParams(params), { eventID: eventId });
    else f("trackCustom", eventName, cleanParams(params));
  });
  return true;
}

/** Mirror a conversion into GA4 when it fires (best effort, never blocking). */
export function mirrorToGa4(eventName: string, params?: Params): void {
  try {
    gtag()?.("event", eventName, cleanParams(params));
  } catch {
    /* ignore */
  }
}
